import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import {
  createLocationTaxRate,
  suggestLocationTaxRate,
  updateLocationTaxRate,
} from '../../api/taxRates.api';
import { reportError } from '../../helpers/reportError';
import {
  TAX_RATE_MAX,
  TAX_RATE_MAX_PERCENT,
  TAX_RATE_MIN,
  percentToTaxRate,
  taxRateToPercent,
} from '../../helpers/taxRates';
import {
  CreateTaxRateDto,
  LocationTaxRateEntity,
  TaxRateDraft,
  TaxRateSuggestion,
  TaxRateSuggestionConfidence,
  TaxRateType,
} from '../../types/taxRate.types';
// Direct file imports on purpose - the styles barrel triggers a circular init.
import { Div } from '../../styles/styles';
import { Disclaimer, H5, P } from '../../styles/text';
import { lightTheme } from '../../styles/theme';
import { AIStarsIcon } from '../../assets/svg';
import { Button } from '../../components/button/button';
import { Modal, PropsModal } from '../../components/modal/modal';
import { AdminDollarInput2 } from '../AdminDollarInput/AdminDollarInput2';
import { AdminInput } from '../AdminInput/AdminInput';
import { AdminSelect } from '../AdminSelect/AdminSelect';
import { AdminSwitch } from '../AdminSwitch/AdminSwitch';
import { Callout } from '../Callout/Callout';
import { FieldSuffix, SourceLink } from './TaxRateFormModal.styled';
import { Icon } from '../../styles/styles';
import { ErrorToast, SuccessToast } from '../Toasts/Toasts';

type Props = PropsModal & {
  /** True to add, a rate to edit, null to stay closed. */
  formModal: LocationTaxRateEntity | TaxRateDraft | boolean | null;
  locationId: string | number;
  /** Not needed in draft mode: nothing is written, so nothing waits. */
  setLoading?: Dispatch<SetStateAction<boolean>>;
  /** Gets the rate the API answered with - the menu picker ticks it. */
  onSaved?: (taxRate: LocationTaxRateEntity) => void;
  /**
   * Hands the values back instead of writing them, and takes the place of
   * `onSaved`. The location verification form works this way: its rates are
   * written by the verify call itself, in the same transaction, so there is
   * nothing to POST here and nothing to undo if the verification fails.
   */
  onDraftSaved?: (taxRate: CreateTaxRateDto) => void;
  /** Raise it above the modal that opened this one. */
  zIndex?: number;
};

/** Up to four decimals: a percentage is stored times 10.000. */
const PERCENT_PATTERN = /^\d*\.?\d{0,4}$/;

/**
 * Whether the answer can be taken at face value. `trusted` is the backend's
 * own verdict and the one the backfill writes on, so it wins; without it the
 * confidence is all there is, and only `high` counts - every wrong answer in
 * the measured sample was a `medium` or a `low`.
 */
const isSuggestionSure = (suggestion: TaxRateSuggestion) =>
  suggestion.trusted ??
  suggestion.confidence === TaxRateSuggestionConfidence.high;

/**
 * Add or edit one of a location's tax rates. Opened from the location's list
 * and from the menu's tax picker, which is why it lives here rather than in
 * either screen.
 *
 * `inclusive` is deliberately not on the form. FoodHub has the flag, but every
 * price we hold is exclusive of tax, so it stays at the backend default of
 * false until someone actually needs the other behaviour.
 */
export const TaxRateFormModal = ({
  formModal,
  closeModal,
  locationId,
  setLoading,
  onSaved,
  onDraftSaved,
  zIndex = 110,
}: Props) => {
  const editing = typeof formModal === 'object' ? formModal : null;
  /** A draft has no id: it is not written until the location is verified. */
  const saved = editing && 'id' in editing ? editing : null;

  const [name, setName] = useState('');
  const [type, setType] = useState<TaxRateType>(TaxRateType.percentage);
  /** Kept as typed so "8." and "8.87" survive mid-edit. */
  const [percent, setPercent] = useState('');
  const [amount, setAmount] = useState(0);
  const [applyToAllItems, setApplyToAllItems] = useState(true);
  const [suggestion, setSuggestion] = useState<TaxRateSuggestion | null>(null);
  const [suggesting, setSuggesting] = useState(false);

  useEffect(() => {
    if (!formModal) return;

    if (editing) {
      setName(editing.name);
      setType(editing.type);
      setPercent(
        editing.type === TaxRateType.percentage
          ? String(taxRateToPercent(editing.rate))
          : ''
      );
      setAmount(editing.type === TaxRateType.fixed ? editing.rate : 0);
      setApplyToAllItems(editing.applyToAllItems);
    } else {
      setName('');
      setType(TaxRateType.percentage);
      setPercent('');
      setAmount(0);
      setApplyToAllItems(true);
    }
    setSuggestion(null);
    // `editing` is derived from `formModal`, so the one dependency covers it.
  }, [formModal]);

  /**
   * Reads the rate off the location's address. Nothing is written: the fields
   * are filled in and a person still presses Confirm, which is the whole point
   * of it being a suggestion. Slow (the model searches the web), so the button
   * says what it is doing rather than going quiet.
   */
  async function handleAutofill() {
    try {
      setSuggesting(true);
      const { data } = await suggestLocationTaxRate(locationId);
      const result = data.data;

      setSuggestion(result);
      setType(TaxRateType.percentage);
      // `ratePercent` is the reading figure; `rate` is the same number already
      // scaled. Either one alone is enough to fill the field.
      setPercent(
        String(
          result.ratePercent ??
            (result.rate != null ? taxRateToPercent(result.rate) : '')
        )
      );
      setApplyToAllItems(true);
      if (!name.trim()) setName('Sales Tax');
    } catch (error) {
      reportError(error, { context: 'suggestLocationTaxRate' });
    } finally {
      setSuggesting(false);
    }
  }

  const typeOptions = (
    <optgroup label="Select Type">
      <option value={TaxRateType.percentage}>Percentage</option>
      <option value={TaxRateType.fixed}>Fixed amount per item</option>
    </optgroup>
  );

  async function handleSubmit() {
    if (!name.trim()) return ErrorToast('Please enter a tax name');

    const rate =
      type === TaxRateType.percentage
        ? percentToTaxRate(Number(percent || 0))
        : amount;

    if (!Number.isFinite(rate) || rate < TAX_RATE_MIN || rate > TAX_RATE_MAX) {
      return ErrorToast(
        type === TaxRateType.percentage
          ? `Percentage must be between 0 and ${TAX_RATE_MAX_PERCENT}.`
          : 'Amount is too high.'
      );
    }

    const body = {
      name: name.trim(),
      rate,
      type,
      applyToAllItems,
    };

    // Draft mode: the caller keeps the values until it has something to write
    // them with. No request, no toast - nothing has happened yet.
    if (onDraftSaved) {
      onDraftSaved(body);
      closeModal?.();
      return;
    }

    try {
      setLoading?.(true);
      const { data } = saved
        ? await updateLocationTaxRate(locationId, saved.id, body)
        : await createLocationTaxRate(locationId, body);

      SuccessToast(data.message);
      onSaved?.(data.data);
      closeModal?.();
    } catch (error) {
      reportError(error, { context: 'saveLocationTaxRate' });
    } finally {
      setLoading?.(false);
    }
  }

  return (
    <Modal
      isOpen={!!formModal}
      closeModal={closeModal}
      header={editing ? 'Edit Tax' : 'Add Tax'}
      logo={false}
      zIndex={zIndex}
      width="450px"
      // The same glow the AI image modal wears while it works, so a lookup
      // that takes 10-20 seconds looks like something is happening.
      glow={suggesting}
      footer={
        <Button $selected $w="100%" onClick={handleSubmit}>
          Confirm
        </Button>
      }
    >
      <Div $flex $column $gap="24px">
        {/* Offered on an edit too: a rate is most often opened again because
        it is wrong, and the lookup is the fastest way to a right one. */}
        <div>
          <Button
            $blueGradient
            $selected
            $w="100%"
            $loading={suggesting}
            onClick={handleAutofill}
          >
            <Div $flex $center $gap="8px">
              <Icon>
                <AIStarsIcon />
              </Icon>
              {suggesting ? 'Looking up the rate' : 'Autofill with AI'}
            </Div>
          </Button>
          <Disclaimer $m="8px 0 0" $center $color={lightTheme.lightGray}>
            Reads the state and city rates for this address. Takes a few
            seconds, and fills the form in for you to check.
          </Disclaimer>
        </div>

        {suggestion && (
          <Callout tone={isSuggestionSure(suggestion) ? 'info' : 'danger'}>
            {suggestion.components.length > 0 && (
              <P $m="0 0 4px" $left>
                {suggestion.components.join(' + ')}
              </P>
            )}
            {!isSuggestionSure(suggestion) && (
              <P $m="0 0 4px" $left $bold>
                Check this one before saving - the lookup was not sure.
              </P>
            )}
            {suggestion.note && (
              <P $m="0 0 4px" $left>
                {suggestion.note}
              </P>
            )}
            {suggestion.sources.map((source) => (
              <Disclaimer key={source} $m="0" $left>
                <SourceLink
                  href={source}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {source}
                </SourceLink>
              </Disclaimer>
            ))}
          </Callout>
        )}

        <AdminInput
          label="Name"
          placeholder="NY Sales Tax"
          value={name}
          onChange={({ target }: { target: HTMLInputElement }) =>
            setName(target.value)
          }
          maxLength={100}
          $textCenter
        />

        <AdminSelect
          label="Type"
          value={type}
          options={typeOptions}
          onChange={({ target }: { target: HTMLSelectElement }) =>
            setType(target.value as TaxRateType)
          }
          $textCenter
        />

        {type === TaxRateType.percentage ? (
          <Div $relative>
            <AdminInput
              label="Percentage"
              placeholder="8.875"
              value={percent}
              onChange={({ target }: { target: HTMLInputElement }) => {
                const next = target.value;
                if (!PERCENT_PATTERN.test(next)) return;
                if (next !== '' && Number(next) > TAX_RATE_MAX_PERCENT) return;
                setPercent(next);
              }}
              $textCenter
            />
            <FieldSuffix>%</FieldSuffix>
          </Div>
        ) : (
          <AdminDollarInput2
            label="Amount per item"
            value={amount}
            onValueChange={(cents: number) => setAmount(cents)}
            max={TAX_RATE_MAX}
            $textCenter
          />
        )}

        {/* The switch sits against the whole text block, label and note
        together, rather than lining up with the label alone. */}
        <Div $box $p="16px 20px" $flex $alignCenter $between $gap="16px">
          <div>
            <H5 $m="0" $left>
              Applies to all items
            </H5>
            <Disclaimer $m="4px 0 0" $left $color={lightTheme.lightGray}>
              {applyToAllItems
                ? 'Charged on every item and option that has no taxes of its own.'
                : 'Charged only where it is set on the menu - an alcohol tax, say.'}
            </Disclaimer>
          </div>
          <AdminSwitch
            checked={applyToAllItems}
            onChange={(e) => setApplyToAllItems(e.target.checked)}
          />
        </Div>

        {saved && (
          <P $m="0" $left $color={lightTheme.darkGray}>
            Changes apply the next time the menu is published.
          </P>
        )}
      </Div>
    </Modal>
  );
};
